/**
 * Connectivity coverage auto-fill — maps SED connectivity slot data onto
 * the `fiber-survey` checklist requirement (carrier + build distance).
 */
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { RequirementAutoFill } from './compileCoverageAutoFill';

export const FIBER_SURVEY_FIELD_KEYS = [
  'carrier',
  'serviceType',
  'capacity_Gbps',
  'buildDistance_ft',
  'quoteValidUntil',
  'mrc_USD',
] as const;

export function mapConnectivitySedToCoverage(
  sed: SiteEvidenceDocument,
): Pick<RequirementAutoFill, 'fields' | 'notes' | 'confidence' | 'fieldsFilledCount' | 'fieldsTotalCount'> {
  const conn = sed.connectivity?.data;
  const fields: Record<string, string | number> = {};
  const hints: string[] = [];

  if (conn?.fiberProvider) {
    fields.carrier = conn.fiberProvider;
    hints.push(`fiber provider ${conn.fiberProvider}`);
  }

  if (typeof conn?.fiberBuildDistanceFt === 'number' && Number.isFinite(conn.fiberBuildDistanceFt)
    && conn.fiberBuildDistanceFt >= 0) {
    fields.buildDistance_ft = conn.fiberBuildDistanceFt;
    hints.push(`source-stated fiber build distance to handoff ${conn.fiberBuildDistanceFt} ft; not corridor proximity or confirmed service`);
  }

  if (typeof conn?.nearestFiberKm === 'number' && Number.isFinite(conn.nearestFiberKm)) {
    hints.push(`nearest mapped fiber ${conn.nearestFiberKm.toFixed(1)} km; this is proximity, not a surveyed construction route`);
  }

  return {
    fields,
    notes: hints.length > 0
      ? `SED connectivity hint: ${hints.join('; ')}. Site-specific carrier quote still required for credible interconnection conclusions.`
      : 'No on-site fiber signal on SED — capture carrier quote or survey.',
    confidence: {
      tier: Object.keys(fields).length >= 2 ? 'low' : 'low',
      value: 0.45,
      rationale: 'SED connectivity inference only — not a carrier quote',
    },
    fieldsFilledCount: Object.keys(fields).length,
    fieldsTotalCount: FIBER_SURVEY_FIELD_KEYS.length,
  };
}

export function connectivitySedToRequirementAutoFill(
  sed: SiteEvidenceDocument,
): RequirementAutoFill | null {
  const mapped = mapConnectivitySedToCoverage(sed);
  if (mapped.fieldsFilledCount === 0) return null;
  return {
    requirementId: 'fiber-survey',
    ...mapped,
    sourceProvider: 'sed-connectivity',
    sourceUrl: '',
  };
}
